/**
 * Utiliti Pemantauan Prestasi (Performance Monitoring Utility)
 * Ditala khusus mengikut piawaian Google Core Web Vitals rasmi dan React Fiber Frame Budget.
 */
import { useEffect, useRef } from 'react';

// Ambang batas masa khusus bagi setiap kategori metrik
const THRESHOLDS = {
  // Komponen React & Operasi UI Segerak (Sync Main-Thread)
  RENDER: {
    FRAME_BUDGET: 16.67, // 60 FPS (1 frame drop jika melebihi)
    BOTTLENECK: 50.0,    // Long-Task / UI freeze
  },
  // Operasi Asinkron (Query Database, Network Fetch, File Parsing)
  ASYNC: {
    FAST: 1000.0,        // < 1.0s sangat pantas untuk I/O
    MODERATE: 2500.0,    // 1.0s - 2.5s sederhana
    SLOW: 4000.0,        // > 4.0s perlahan
  },
  // Google Core Web Vitals (Piawaian Industri Rasmi)
  LCP: {
    GOOD: 2500.0,        // <= 2.5s adalah Baik (Hijau)
    POOR: 4000.0,        // > 4.0s adalah Lemah (Merah)
  },
  FID: {
    GOOD: 100.0,         // <= 100ms adalah Baik (Hijau)
    POOR: 300.0,         // > 300ms adalah Lemah (Merah)
  },
  CLS: {
    GOOD: 0.1,           // <= 0.1 adalah Baik (Hijau)
    POOR: 0.25,          // > 0.25 adalah Lemah (Merah)
  },
};

// Sejarah metrik prestasi dalam ingatan
const perfStore = {
  renders: [],
  operations: [],
  longTasks: [],
  enabled: true,
};

// Penjejak bilangan render global bagi setiap komponen
const globalRenderCounts = new Map();

/**
 * Semak sama ada logging diaktifkan (lalai: aktif)
 */
function isLoggingEnabled() {
  if (typeof window === 'undefined') return false;
  const stored = localStorage.getItem('mtsr_perf_log');
  if (stored !== null) return stored === 'true';
  return perfStore.enabled;
}

/**
 * Nilai tahap prestasi (Rating) mengikut kategori sebenar
 */
function evaluateRating(tag, name, duration) {
  const upperName = (name || '').toUpperCase();

  // 1. Core Web Vitals: LCP (Largest Contentful Paint)
  if (tag === 'WEB VITAL' && upperName.includes('LCP')) {
    if (duration <= THRESHOLDS.LCP.GOOD) {
      return { badgeColor: '#10B981', statusIcon: '⚡ [BAIK]', level: 'info', rating: 'good' };
    }
    if (duration <= THRESHOLDS.LCP.POOR) {
      return { badgeColor: '#F59E0B', statusIcon: '⚠️ [SEDERHANA]', level: 'warn', rating: 'needs-improvement' };
    }
    return { badgeColor: '#EF4444', statusIcon: '🚨 [LEMAH]', level: 'warn', rating: 'poor' };
  }

  // 2. Core Web Vitals: FID (First Input Delay)
  if (tag === 'WEB VITAL' && upperName.includes('FID')) {
    if (duration <= THRESHOLDS.FID.GOOD) {
      return { badgeColor: '#10B981', statusIcon: '⚡ [BAIK]', level: 'info', rating: 'good' };
    }
    if (duration <= THRESHOLDS.FID.POOR) {
      return { badgeColor: '#F59E0B', statusIcon: '⚠️ [SEDERHANA]', level: 'warn', rating: 'needs-improvement' };
    }
    return { badgeColor: '#EF4444', statusIcon: '🚨 [LEMAH]', level: 'warn', rating: 'poor' };
  }

  // 3. Operasi Asinkron / Network I/O
  if (tag === 'ASYNC OP') {
    if (duration <= THRESHOLDS.ASYNC.FAST) {
      return { badgeColor: '#10B981', statusIcon: '⚡', level: 'info', rating: 'fast' };
    }
    if (duration <= THRESHOLDS.ASYNC.MODERATE) {
      return { badgeColor: '#3B82F6', statusIcon: '⏱️', level: 'info', rating: 'moderate' };
    }
    return { badgeColor: '#F59E0B', statusIcon: '⚠️ [SLOW I/O]', level: 'warn', rating: 'slow' };
  }

  // 4. Long Task Browser (> 50ms menyekat main thread)
  if (tag === 'LONG TASK') {
    return { badgeColor: '#EF4444', statusIcon: '🚨 [LONG TASK]', level: 'warn', rating: 'long-task' };
  }

  // 5. Komponen React & Operasi Segerak (UI Frame Budget: 16.67ms)
  if (duration >= THRESHOLDS.RENDER.BOTTLENECK) {
    return { badgeColor: '#EF4444', statusIcon: '🚨 [BOTTLENECK]', level: 'warn', rating: 'bottleneck' };
  }
  if (duration >= THRESHOLDS.RENDER.FRAME_BUDGET) {
    return { badgeColor: '#F59E0B', statusIcon: '⚠️ [DROPPED FRAME]', level: 'warn', rating: 'dropped-frame' };
  }

  return { badgeColor: '#10B981', statusIcon: '⚡', level: 'info', rating: 'smooth' };
}

/**
 * Format teks konsol dengan warna estetik & standard badge
 */
function logMetric(tag, name, duration, meta = {}) {
  if (!isLoggingEnabled()) return;

  const { badgeColor, statusIcon, level, rating } = evaluateRating(tag, name, duration);
  const ms = duration.toFixed(2);

  const tagStyle = `background: ${badgeColor}; color: #ffffff; padding: 2px 6px; border-radius: 4px; font-weight: 700; font-size: 11px;`;
  const timeStyle = `font-weight: 700; color: ${badgeColor}; font-family: monospace; font-size: 12px;`;
  const infoStyle = 'color: #888888; font-size: 11px;';

  const fullMeta = { ...meta, rating };
  const metaStr = Object.keys(fullMeta).length ? `| ${JSON.stringify(fullMeta)}` : '';
  const message = `%c${statusIcon} ${tag}%c ${name}: %c${ms} ms %c${metaStr}`;

  if (level === 'warn') {
    console.warn(message, tagStyle, 'font-weight: bold;', timeStyle, infoStyle);
  } else {
    console.log(message, tagStyle, 'font-weight: bold;', timeStyle, infoStyle);
  }
}

/**
 * Hook untuk menjejak dan merekod masa render mana-mana komponen React.
 */
export function useRenderProfiler(componentName, propsSnapshot = null) {
  const renderStartTime = performance.now();
  const isMount = useRef(true);

  // Kiraan render progresif yang tidak reset pada StrictMode remount
  const prevCount = globalRenderCounts.get(componentName) || 0;
  const currentRender = prevCount + 1;
  globalRenderCounts.set(componentName, currentRender);

  useEffect(() => {
    const duration = performance.now() - renderStartTime;
    const phase = isMount.current ? 'mount' : 'update';
    isMount.current = false;

    perfStore.renders.push({
      component: componentName,
      phase,
      duration,
      renderCount: currentRender,
      timestamp: Date.now(),
    });

    logMetric('RENDER', `${componentName} (#${currentRender} - ${phase})`, duration, {
      ...(propsSnapshot ? { snapshot: propsSnapshot } : {}),
    });
  });
}

/**
 * Pengendali onRender untuk komponen <React.Profiler id="..." onRender={onReactProfilerRender}>
 */
export function onReactProfilerRender(id, phase, actualDuration, baseDuration, startTime, commitTime) {
  perfStore.renders.push({
    component: id,
    phase,
    duration: actualDuration,
    baseDuration,
    startTime,
    commitTime,
    timestamp: Date.now(),
  });

  logMetric('PROFILER', `${id} [${phase}]`, actualDuration, {
    baseDuration: `${baseDuration.toFixed(1)}ms`,
  });
}

/**
 * Mengukur masa eksekusi fungsi segerak (synchronous function)
 */
export function measureSync(label, fn, meta = {}) {
  const start = performance.now();
  try {
    const result = fn();
    const duration = performance.now() - start;
    perfStore.operations.push({ label, duration, timestamp: Date.now(), ...meta });
    logMetric('SYNC OP', label, duration, meta);
    return result;
  } catch (error) {
    const duration = performance.now() - start;
    logMetric('OP ERROR', `${label} (FAILED)`, duration, { error: error?.message });
    throw error;
  }
}

/**
 * Mengukur masa eksekusi fungsi tak segerak (asynchronous / Promise)
 */
export async function measureAsync(label, asyncFn, meta = {}) {
  const start = performance.now();
  try {
    const result = await (typeof asyncFn === 'function' ? asyncFn() : asyncFn);
    const duration = performance.now() - start;
    perfStore.operations.push({ label, duration, timestamp: Date.now(), ...meta });
    logMetric('ASYNC OP', label, duration, meta);
    return result;
  } catch (error) {
    const duration = performance.now() - start;
    logMetric('ASYNC ERROR', `${label} (FAILED)`, duration, { error: error?.message });
    throw error;
  }
}

/**
 * Pengukur manual (Start & Stop)
 */
const activeTimers = new Map();

export function startTimer(label) {
  activeTimers.set(label, performance.now());
}

export function stopTimer(label, meta = {}) {
  const start = activeTimers.get(label);
  if (start == null) return 0;
  const duration = performance.now() - start;
  activeTimers.delete(label);
  perfStore.operations.push({ label, duration, timestamp: Date.now(), ...meta });
  logMetric('TIMER', label, duration, meta);
  return duration;
}

/**
 * Inisialisasi pemerhati prestasi browser (PerformanceObserver)
 */
export function initPerformanceMonitoring() {
  if (typeof window === 'undefined') return;

  // 1. Pantau Long Tasks (> 50ms) yang menyekat UI thread
  if ('PerformanceObserver' in window && PerformanceObserver.supportedEntryTypes?.includes('longtask')) {
    try {
      const longTaskObserver = new PerformanceObserver((entryList) => {
        for (const entry of entryList.getEntries()) {
          perfStore.longTasks.push({
            duration: entry.duration,
            startTime: entry.startTime,
            name: entry.name,
          });

          logMetric('LONG TASK', 'Main Thread Freeze', entry.duration, {
            startTime: `${entry.startTime.toFixed(1)}ms`,
            attribution: entry.attribution?.[0]?.name || 'script',
          });
        }
      });
      longTaskObserver.observe({ entryTypes: ['longtask'] });
    } catch {
      /* abaikan */
    }
  }

  // 2. Pantau Core Web Vitals (LCP, FID/INP)
  if ('PerformanceObserver' in window) {
    try {
      // Largest Contentful Paint (LCP)
      if (PerformanceObserver.supportedEntryTypes?.includes('largest-contentful-paint')) {
        const lcpObserver = new PerformanceObserver((entryList) => {
          const entries = entryList.getEntries();
          const lastEntry = entries[entries.length - 1];
          if (lastEntry) {
            logMetric('WEB VITAL', 'LCP (Largest Contentful Paint)', lastEntry.startTime, {
              element: lastEntry.element?.tagName || 'unknown',
            });
          }
        });
        lcpObserver.observe({ type: 'largest-contentful-paint', buffered: true });
      }

      // First Input Delay (FID)
      if (PerformanceObserver.supportedEntryTypes?.includes('first-input')) {
        const fidObserver = new PerformanceObserver((entryList) => {
          for (const entry of entryList.getEntries()) {
            const delay = entry.processingStart - entry.startTime;
            logMetric('WEB VITAL', 'FID (First Input Delay)', delay, {
              eventType: entry.name,
            });
          }
        });
        fidObserver.observe({ type: 'first-input', buffered: true });
      }
    } catch {
      /* abaikan */
    }
  }

  // 3. API interaktif konsol window.__PERF__
  window.__PERF__ = {
    getStats: () => {
      const topSlowRenders = [...perfStore.renders].sort((a, b) => b.duration - a.duration).slice(0, 10);
      const topSlowOps = [...perfStore.operations].sort((a, b) => b.duration - a.duration).slice(0, 10);
      console.table(topSlowRenders);
      console.table(topSlowOps);
      return {
        totalRenders: perfStore.renders.length,
        totalOperations: perfStore.operations.length,
        totalLongTasks: perfStore.longTasks.length,
        topSlowRenders,
        topSlowOps,
      };
    },
    clear: () => {
      perfStore.renders = [];
      perfStore.operations = [];
      perfStore.longTasks = [];
      globalRenderCounts.clear();
      console.info('[MTSRpay Perf] Sejarah prestasi telah dibersihkan.');
    },
    enable: () => {
      perfStore.enabled = true;
      localStorage.setItem('mtsr_perf_log', 'true');
      console.info('[MTSRpay Perf] Logging prestasi DIAKTIFKAN.');
    },
    disable: () => {
      perfStore.enabled = false;
      localStorage.setItem('mtsr_perf_log', 'false');
      console.info('[MTSRpay Perf] Logging prestasi DINYAHAKTIFKAN.');
    },
  };

  console.info('[MTSRpay Perf] Pemantau prestasi sedia. Taip window.__PERF__.getStats() di konsol untuk analisa.');
}
